import { ReactNode, useMemo } from 'react';
import { TZDate } from '@date-fns/tz';

import { Facility } from '@crossnokaye/typescript-sdk/models/components';
import { ShutdownBannerTemplate } from '@crossnokaye/ux-common';

import { ShutdownStatus, State } from '@repo/sdk/models/components';

import { CancelShutdownAction } from '@/components/shutdown-banner/cancel-shutdown-action';
import { ExtendShutdownAction } from '@/components/shutdown-banner/extend-shutdown-action';
import { RestartBuildingAction } from '@/components/shutdown-banner/restart-building-action';
import { useShutdownBanner } from '@/hooks/query-hooks/gaia-api/shutdown-banner';
import { useCurrentFacility } from '@/hooks/use-current-facility';

const formatDateTime = (date: Date, tz: string) => {
  const dateInTz = new TZDate(date, tz);
  return {
    date: dateInTz.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }),
    time: dateInTz.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    }),
  };
};

// Parse the shutdown reason
const parseReason = (reason: string | undefined) => {
  if (!reason) return '';
  switch (reason.toUpperCase()) {
    case 'DEMAND_RESPONSE':
      return ' due to a demand response event';
    case 'RATE_OPTIMIZATION':
      return ' to optimize energy costs';
    case 'SCHEDULED':
      return ' as scheduled';
    default:
      return '';
  }
};

type BannerProps = {
  actions: ReactNode[];
  title: string;
  variant: 'pumpdown' | 'shutdown' | 'scheduled';
};

// Get the banner props based on the Shutdown state
const getBannerProps = ({
  facility,
  shutdownStatus,
}: {
  facility: Facility | undefined;
  shutdownStatus: ShutdownStatus | null | undefined;
}): BannerProps => {
  if (!shutdownStatus || !facility) return { actions: [], title: '', variant: 'shutdown' };
  // Get local timezone from browser
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Use facility timezone if available, otherwise fallback to local
  const tz = facility?.timezone || localTz;
  switch (shutdownStatus.state) {
    case State.Pumpdown: {
      const { date, time } = formatDateTime(shutdownStatus.startTime!, tz);
      return {
        actions: [<RestartBuildingAction key="restart" />],
        title: `${facility.displayName} is in pumpdown and will shut down at ${time} on ${date}.`,
        variant: 'pumpdown',
      };
    }
    case State.AutoShutdown: {
      const { date: startDate, time: startTime } = formatDateTime(shutdownStatus.startTime!, tz);
      const { date: endDate, time: endTime } = formatDateTime(shutdownStatus.endTime!, tz);
      return {
        actions: [
          <ExtendShutdownAction key="extend" endTime={shutdownStatus.endTime} />,
          <RestartBuildingAction key="restart" />,
        ],
        title: `${facility.displayName} was shut down${parseReason(shutdownStatus.reason)} at ${startTime} on ${startDate}, and will restart at ${endTime} on ${endDate}.`,
        variant: 'shutdown',
      };
    }
    case State.ManualShutdown: {
      const { date, time } = formatDateTime(shutdownStatus.startTime!, tz);
      return {
        actions: [<RestartBuildingAction key="restart" />],
        title: `${facility.displayName} was shut down at ${time} on ${date}.`,
        variant: 'shutdown',
      };
    }
    case State.Scheduled: {
      const { date, time } = formatDateTime(shutdownStatus.startTime!, tz);
      return {
        actions: [
          <CancelShutdownAction
            key="cancel"
            endTime={shutdownStatus.endTime}
            startTime={shutdownStatus.startTime}
          />,
        ],
        title: `ATLAS has scheduled a shutdown in order to reduce your electrical spend due to upcoming high rates. Your facility will begin shutting down at ${time} on ${date}.`,
        variant: 'scheduled',
      };
    }
    default:
      return { actions: [], title: '', variant: 'shutdown' };
  }
};

export const ShutdownBanner = () => {
  const { agentId, currentFacility: facility } = useCurrentFacility();
  const { data: shutdownStatus } = useShutdownBanner(agentId);

  const bannerProps = useMemo(
    () =>
      getBannerProps({
        facility,
        shutdownStatus,
      }),
    [facility, shutdownStatus]
  );

  return <ShutdownBannerTemplate {...bannerProps} />;
};
